/* eslint-disable jsx-a11y/no-redundant-roles */
import { safeHref } from '../../../utils/common/safe-url';
import React, { useEffect, useState } from 'react';
import { getCreatorRequest } from '../../../api/admin/user.api';
import { DocumentType, VerificationRequest, VerificationStatus } from '../../../models/verification-request.model';
import { Loader2 } from 'lucide-react';
import DialogComponent from '../../../components/dialog/dialog.component';

interface CreatorRequestDialogProps {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
}

const CreatorRequestDialog: React.FC<CreatorRequestDialogProps> = ({ userId, isOpen, onClose }) => {
  const [request, setRequest] = useState<VerificationRequest | null>(null);
  const [loading, setLoading] = useState(true);
  console.log(isOpen);

  useEffect(() => {
    if (isOpen && userId) {
      loadRequestDetails();
    }
  }, [isOpen, userId]);

  const loadRequestDetails = async () => {
    try {
      setLoading(true);
      const data = await getCreatorRequest(userId);
      setRequest(data);
    } catch (error) {
      console.error('Error loading creator request:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentTypeName = (type: DocumentType) => {
    switch (type) {
      case DocumentType.IdentityCard:
        return 'CMND/CCCD';
      case DocumentType.Passport:
        return 'Hộ chiếu';
      case DocumentType.DriverLicense:
        return 'Giấy phép lái xe';
      default:
        return 'Không xác định';
    }
  };

  const getStatusName = (status: VerificationStatus) => {
    switch (status) {
      case VerificationStatus.Pending:
        return 'Đang chờ xác minh';
      case VerificationStatus.Approved:
        return 'Đã xác minh';
      case VerificationStatus.Rejected:
        return 'Đã từ chối';
      default:
        return 'Không xác định';
    }
  };

  const getStatusColor = (status: VerificationStatus) => {
    switch (status) {
      case VerificationStatus.Pending:
        return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20';
      case VerificationStatus.Approved:
        return 'bg-green-50 text-green-700 ring-green-600/20';
      case VerificationStatus.Rejected:
        return 'bg-red-50 text-red-700 ring-red-600/20';
      default:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20';
    }
  };

  return (
    <DialogComponent visible={isOpen} onClose={onClose} title="Chi tiết yêu cầu xác minh" size="lg" placement="CENTER">
      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : request ? (
        <div className="mt-6 border-t border-gray-100">
          <dl className="divide-y divide-gray-100">
            <div className="px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">Tên hiển thị</dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{request.nameOrther}</dd>
            </div>

            <div className="px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">Quốc gia</dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{request.nationName}</dd>
            </div>

            <div className="px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">Trạng thái</dt>
              <dd className="mt-1 sm:col-span-2 sm:mt-0">
                <span
                  className={`inline-flex items-center rounded-md px-2 py-1 text-sm font-medium ring-1 ring-inset ${getStatusColor(
                    request.status,
                  )}`}
                >
                  {getStatusName(request.status)}
                </span>
              </dd>
            </div>

            <div className="px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">Loại giấy tờ</dt>
              <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                {getDocumentTypeName(request.documentType)}
              </dd>
            </div>

            <div className="px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">Liên kết tham khảo</dt>
              <dd className="mt-2 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                <ul role="list" className="divide-y divide-gray-100 rounded-md border border-gray-200">
                  {/* Hồ sơ thiếu liên kết (vd tài khoản được đánh dấu yêu cầu mà không qua form) trước đây làm sập cả trang admin. */}
                  {(request.referenceLinks ?? []).length === 0 && (
                    <li className="py-4 pl-4 pr-5 text-sm leading-6 text-gray-500">Không có liên kết.</li>
                  )}
                  {(request.referenceLinks ?? []).map((link, index) => (
                    <li key={index} className="flex items-center justify-between py-4 pl-4 pr-5 text-sm leading-6">
                      <div className="flex w-0 flex-1 items-center">
                        <div className="ml-4 flex min-w-0 flex-1 gap-2">
                          <a
                            href={safeHref(link)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="truncate font-medium text-blue-600 hover:text-blue-500"
                          >
                            {link}
                          </a>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </dd>
            </div>

            <div className="px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm font-medium leading-6 text-gray-900">Giấy tờ xác minh</dt>
              <dd className="mt-2 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {request.documentLink && (
                  <div className="rounded-md border border-gray-200 p-4">
                    <img
                      src={request.documentLink}
                      alt="Giấy tờ xác minh"
                      className="max-h-[300px] w-full rounded-lg object-contain"
                    />
                  </div>
                )}
              </dd>
            </div>
          </dl>
        </div>
      ) : (
        <div className="flex h-48 items-center justify-center text-sm text-gray-500">
          Không tìm thấy thông tin yêu cầu xác minh
        </div>
      )}
    </DialogComponent>
  );
};

export default CreatorRequestDialog;
